import { useState, useEffect } from "react";
import { useApolloClient } from "@apollo/client";
import {
  Group,
  Button,
  Avatar,
  Text,
  useMantineTheme,
  SelectItem,
} from "@mantine/core";
import { forwardRef } from "react";
import Select from "src/components/Select";
import {
  AttendeeType,
  GetAttendeePageDocument,
  GetAttendeePageQuery,
  GetAttendeePageQueryVariables,
  GetClientPageQuery,
  GetClientPageQueryVariables,
} from "src/generated/graphql";
import { ArrayElement } from "src/utils/helperTypes";
import { debounce } from "src/utils/utils";
import { MeetingTypes } from "src/utils/constants";
import { GetClientPage } from "src/api/graphql/queries/Client";

type SearchTypeList =
  | GetAttendeePageQuery["attendeePage"]["items"]
  | GetClientPageQuery["clientPage"]["items"];

interface SearchListProps {
  type: MeetingTypes;
  onCancel: () => void;
  onSubmit: (values: string) => void;
  currentIdList: string[];
  waitlisteeClients?: string[];
}

const ItemSelect = forwardRef<HTMLDivElement, SelectItem>(
  ({ image, label, description, ...others }: SelectItem, ref) => (
    <div ref={ref} {...others}>
      <Group noWrap>
        {image && <Avatar src={image} />}

        <div>
          <Text size="sm">{label}</Text>
          <Text size="xs" opacity={0.65}>
            {description}
          </Text>
        </div>
      </Group>
    </div>
  )
);

export const SearchList = ({
  type,
  onCancel,
  onSubmit,
  currentIdList,
  waitlisteeClients,
}: SearchListProps) => {
  const [searchedTypeList, setSearchTypeList] = useState<SelectItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<string | null>("");
  const [value, setvalue] = useState("");

  const theme = useMantineTheme();
  const { query } = useApolloClient();
  const onSearchList = (searchValue: string, isFirstTime = false) => {
    setvalue(searchValue);
    // Checking if selected id exists in the previous search item list
    const isSameItem = searchedTypeList.some(
      (searchListItem) => searchListItem.value === selectedItem
    );
    if ((!searchValue && !isFirstTime) || isSameItem) {
      return;
    }
    const variables = {
      pagination: {
        count: 10,
        page: 1,
        filters: [
          {
            field: "name",
            type: "text",
            value: searchValue,
            ...(type === MeetingTypes.Waitlistee && {
              parentRelationField: "user",
            }),
          },
        ],
      },
    };

    if (type === MeetingTypes.Waitlistee) {
      query<GetClientPageQuery, GetClientPageQueryVariables>({
        query: GetClientPage,
        variables,
      }).then(({ data }) => {
        setSearchTypeList(
          normalizeList(
            data.clientPage.items.filter(
              (item) => !currentIdList.includes(item.id)
            )
          )
        );
      });
    } else {
      query<GetAttendeePageQuery, GetAttendeePageQueryVariables>({
        query: GetAttendeePageDocument,
        variables,
      }).then(({ data }) => {
        //filter Data based on currentAttendee and waitlistees
        let _filterData = data.attendeePage.items.filter(
          (item) => !currentIdList.includes(item.id)
        );
        if (waitlisteeClients?.length) {
          _filterData = _filterData.filter(
            (e) => !waitlisteeClients.includes(e.client?.id || "")
          );
        }

        setSearchTypeList(normalizeList(_filterData));
      });
    }
  };

  useEffect(() => {
    // onFirst time call api
    onSearchList("", true);
  }, []);

  const normalizeList = (list: SearchTypeList): SelectItem[] => {
    if (!list.length) {
      return [];
    }
    const itemList = list.map((item) => {
      const { id, client, user } = item as ArrayElement<
        GetAttendeePageQuery["attendeePage"]["items"]
      > &
        ArrayElement<GetClientPageQuery["clientPage"]["items"]>;
      return {
        label:
          type === MeetingTypes.Waitlistee
            ? `${user.firstName} ${user.lastName}`
            : `${client!.user.firstName} ${client!.user.lastName}`,
        value: id,
      } as SelectItem;
    });
    return itemList;
  };

  const onDebouncedSearch = debounce(onSearchList, 500);
  return (
    <>
      <Select
        data={searchedTypeList}
        placeholder={"Search"}
        itemComponent={ItemSelect}
        searchable
        clearable
        maxDropdownHeight={500}
        nothingFound={value ? "No result found!" : "Please type something!"}
        onSearchChange={onDebouncedSearch}
        onChange={setSelectedItem}
      />
      <Group position="apart" mt="md" style={{ marginTop: 30 }}>
        <Button
          radius="md"
          variant="outline"
          onClick={onCancel}
          style={{
            flex: 1,
            color: theme.colors.secondaryTextColor[6],
          }}
          sx={{
            backgroundColor: theme.colors.secondary[6],
            "&:hover": {
              background: theme.colors.secondary[5],
            },
          }}
        >
          Cancel
        </Button>
        <Button
          radius="md"
          onClick={() => onSubmit(selectedItem!)}
          disabled={!selectedItem}
          style={{
            flex: 1,
          }}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
        >
          Add
        </Button>
      </Group>
    </>
  );
};
